"use client";

import { useState } from "react";
import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { GridContainer } from "@/components/layout/GridContainer";
import { ListBlock } from "@/components/layout/ListBlock";
import { SignupModal } from "@/components/layout/SignupModal";
import { MembershipPaymentModal } from "@/components/membership/MembershipPaymentModal";
import { PraxisContactModal } from "@/components/praxis/PraxisContactModal";
import { type MaskShapeVariant } from "@/components/illustrations/MaskShape";
import type { AssetStoryblok, MultilinkStoryblok } from "@/component-types-sb";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

type InfoHeroStoryblok = {
  subline?: string;
  headline?: string;
  text?: string;
  image?: AssetStoryblok;
  image_mask_shape?: MaskShapeVariant;
  list_items?: any[];
  button_label?: string;
  button_link?: MultilinkStoryblok;
  button_type?: string;
  disclaimer?: string;
  _uid: string;
  component: "info_hero";
  [k: string]: any;
};

type InfoHeroProps = {
  blok: InfoHeroStoryblok;
};

// Normalized clip path shapes (0-1 range) for inline SVG masking.
// Matches MaskShapeClipPath in HeroEvent for consistency.
const MaskShapeClipPath = ({ variant }: { variant: MaskShapeVariant }) => {
  switch (variant) {
    case "four_vertical_even":
      return (
        <>
          <path d="M0.363 0.5C0.363 0.776 0.282 1 0.182 1C0.081 1 0 0.776 0 0.5C0 0.224 0.081 0 0.182 0C0.282 0 0.363 0.224 0.363 0.5Z" />
          <path d="M0.576 0.5C0.576 0.776 0.494 1 0.394 1C0.294 1 0.212 0.776 0.212 0.5C0.212 0.224 0.294 0 0.394 0C0.494 0 0.576 0.224 0.576 0.5Z" />
          <path d="M0.788 0.5C0.788 0.776 0.706 1 0.606 1C0.506 1 0.424 0.776 0.424 0.5C0.424 0.224 0.506 0 0.606 0C0.706 0 0.788 0.224 0.788 0.5Z" />
          <path d="M1 0.5C1 0.776 0.919 1 0.818 1C0.718 1 0.637 0.776 0.637 0.5C0.637 0.224 0.718 0 0.818 0C0.919 0 1 0.224 1 0.5Z" />
        </>
      );
    case "two_vertical_uneven":
      return (
        <>
          <path d="M0.4 0.5C0.4 0.776 0.31 1 0.2 1C0.09 1 0 0.776 0 0.5C0 0.224 0.09 0 0.2 0C0.31 0 0.4 0.224 0.4 0.5Z" />
          <path d="M1 0.5C1 0.776 0.838 1 0.638 1C0.437 1 0.275 0.776 0.275 0.5C0.275 0.224 0.437 0 0.638 0C0.838 0 1 0.224 1 0.5Z" />
        </>
      );
    case "wave":
      return (
        <>
          <path d="M0.419 0.5C0.419 0.224 0.502 0 0.605 0C0.707 0 0.791 0.224 0.791 0.5H0.419Z" />
          <path d="M0 0.5C0 0.224 0.083 0 0.186 0C0.289 0 0.372 0.224 0.372 0.5H0Z" />
          <path d="M1 0.5C1 0.776 0.917 1 0.814 1C0.711 1 0.628 0.776 0.628 0.5H1Z" />
          <path d="M0.581 0.5C0.581 0.776 0.498 1 0.395 1C0.293 1 0.209 0.776 0.209 0.5H0.581Z" />
        </>
      );
    case "split_half_circle":
      return (
        <>
          <path d="M0 1C0.277 1 0.501 0.776 0.501 0.5C0.501 0.224 0.277 0 0 0V1Z" />
          <path d="M0.501 1C0.777 1 1 0.776 1 0.5C1 0.224 0.777 0 0.501 0V1Z" />
        </>
      );
    case "three_horizontal_even":
      return (
        <>
          <path d="M1 0.225C1 0.349 0.776 0.45 0.5 0.45C0.224 0.45 0 0.349 0 0.225C0 0.101 0.224 0 0.5 0C0.776 0 1 0.101 1 0.225Z" />
          <path d="M1 0.5C1 0.624 0.776 0.725 0.5 0.725C0.224 0.725 0 0.624 0 0.5C0 0.376 0.224 0.275 0.5 0.275C0.776 0.275 1 0.376 1 0.5Z" />
          <path d="M1 0.775C1 0.899 0.776 1 0.5 1C0.224 1 0 0.899 0 0.775C0 0.651 0.224 0.55 0.5 0.55C0.776 0.55 1 0.651 1 0.775Z" />
        </>
      );
    case "three_vertical_uneven":
      return (
        <>
          <path d="M0.4 0.5C0.4 0.776 0.31 1 0.2 1C0.09 1 0 0.776 0 0.5C0 0.224 0.09 0 0.2 0C0.31 0 0.4 0.224 0.4 0.5Z" />
          <path d="M0.6 0.5C0.6 0.776 0.51 1 0.4 1C0.29 1 0.2 0.776 0.2 0.5C0.2 0.224 0.29 0 0.4 0C0.51 0 0.6 0.224 0.6 0.5Z" />
          <path d="M1 0.5C1 0.776 0.869 1 0.708 1C0.546 1 0.415 0.776 0.415 0.5C0.415 0.224 0.546 0 0.708 0C0.869 0 1 0.224 1 0.5Z" />
        </>
      );
    case "three_vertical_uneven_mirrored":
      return (
        <>
          <path d="M1 0.5C1 0.77625 0.9105 1 0.8 1C0.6895 1 0.6 0.77625 0.6 0.5C0.6 0.22375 0.6895 0 0.8 0C0.9105 0 1 0.22375 1 0.5Z" />
          <path d="M0.785 0.5C0.785 0.77625 0.6955 1 0.585 1C0.4745 1 0.385 0.77625 0.385 0.5C0.385 0.22375 0.4745 0 0.585 0C0.6955 0 0.785 0.22375 0.785 0.5Z" />
          <path d="M0.585 0.5C0.585 0.77625 0.454 1 0.2925 1C0.131 1 0 0.77625 0 0.5C0 0.22375 0.131 0 0.2925 0C0.454 0 0.585 0.22375 0.585 0.5Z" />
        </>
      );
    case "circle":
      return <circle cx="0.5" cy="0.5" r="0.5" />;
    default:
      return <circle cx="0.5" cy="0.5" r="0.5" />;
  }
};

const InfoHero = ({ blok }: InfoHeroProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [praxisContactOpen, setPraxisContactOpen] = useState(false);

  const buttonHref =
    getUrlFromLinkObject(blok.button_link) || undefined;

  const checkoutEnabled = process.env.NEXT_PUBLIC_CHECKOUT_ENABLED === "true";
  const buttonType = blok.button_type || "link";
  const isMembershipPayment = buttonType === "membership_payment" && checkoutEnabled;
  const isPraxisContact = buttonType === "praxis_contact";
  const signupTopic = buttonType !== "link" && buttonType !== "membership_payment" && buttonType !== "praxis_contact" ? buttonType : undefined;
  const hasButton = blok.button_label && (buttonHref || signupTopic || isMembershipPayment || isPraxisContact);

  const hasImage = !!blok.image?.filename;
  const maskShape: MaskShapeVariant = blok.image_mask_shape || "four_vertical_even";

  return (
    <section
      className="bg-sand-50 pt-6 pb-6 md:pt-8 md:pb-8 break-container"
      {...storyblokEditable(blok)}
    >
      <GridContainer className="lg:!px-0">
        <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
          {/* Shape Masked Image (optional) */}
          {hasImage && (
            <div className="mb-6 md:mb-8">
              {/* Mobile */}
              <div className="md:hidden relative w-full aspect-[345/167] overflow-hidden rounded-2xl">
                <svg
                  viewBox="0 0 345 167"
                  className="w-full h-full"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={blok.image!.alt || blok.headline || ""}
                >
                  <defs>
                    <clipPath
                      id={`info-hero-mask-m-${blok._uid}`}
                      clipPathUnits="objectBoundingBox"
                    >
                      <MaskShapeClipPath variant={maskShape} />
                    </clipPath>
                  </defs>
                  <image
                    href={blok.image!.filename}
                    width="345"
                    height="167"
                    preserveAspectRatio="xMidYMid slice"
                    clipPath={`url(#info-hero-mask-m-${blok._uid})`}
                  />
                </svg>
              </div>
              {/* Desktop */}
              <div className="hidden md:block relative w-full aspect-[345/110] overflow-hidden rounded-2xl">
                <svg
                  viewBox="0 0 345 110"
                  className="w-full h-full"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={blok.image!.alt || blok.headline || ""}
                >
                  <defs>
                    <clipPath
                      id={`info-hero-mask-d-${blok._uid}`}
                      clipPathUnits="objectBoundingBox"
                    >
                      <MaskShapeClipPath variant={maskShape} />
                    </clipPath>
                  </defs>
                  <image
                    href={blok.image!.filename}
                    width="345"
                    height="110"
                    preserveAspectRatio="xMidYMid slice"
                    clipPath={`url(#info-hero-mask-d-${blok._uid})`}
                  />
                </svg>
              </div>
            </div>
          )}

          {/* Subline + Headline */}
          <div className="flex flex-col gap-1">
            {blok.subline && (
              <p className="text-subline-sm text-black">{blok.subline}</p>
            )}
            {blok.headline && (
              <h1 className="text-title-event text-black">{blok.headline}</h1>
            )}
          </div>

          {/* Body text */}
          {blok.text && (
            <p className="text-copy text-black mt-6 md:mt-8">
              {blok.text}
            </p>
          )}

          {/* List items */}
          {blok.list_items && blok.list_items.length > 0 && (
            <ListBlock
              variant="divider"
              className="mt-6 md:mt-8 md:max-w-xl text-black"
            >
              {blok.list_items.map((nestedBlok: any) => (
                <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
              ))}
            </ListBlock>
          )}

          {/* CTA Button + Footnote */}
          {hasButton && (
            <div className="flex flex-col gap-1 mt-6 md:mt-8 p-2 max-w-[420px] mx-auto">
              {isMembershipPayment ? (
                <Button variant="sun" className="w-full" onClick={() => setPaymentModalOpen(true)}>
                  {blok.button_label}
                </Button>
              ) : isPraxisContact ? (
                <Button variant="sun" className="w-full" onClick={() => setPraxisContactOpen(true)}>
                  {blok.button_label}
                </Button>
              ) : signupTopic ? (
                <Button variant="sun" className="w-full" onClick={() => setModalOpen(true)}>
                  {blok.button_label}
                </Button>
              ) : (
                <Button variant="sun" className="w-full" asChild>
                  <Link href={buttonHref!}>{blok.button_label}</Link>
                </Button>
              )}
              {blok.disclaimer && (
                <p className="text-copy-sm text-black-80">
                  {blok.disclaimer}
                </p>
              )}
            </div>
          )}
        </div>
      </GridContainer>

      {signupTopic && (
        <SignupModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          signupTopic={signupTopic}
          buttonLabel={blok.button_label}
        />
      )}
      {isMembershipPayment && (
        <MembershipPaymentModal
          open={paymentModalOpen}
          onOpenChange={setPaymentModalOpen}
          source="info_hero"
        />
      )}
      {isPraxisContact && (
        <PraxisContactModal
          open={praxisContactOpen}
          onOpenChange={setPraxisContactOpen}
        />
      )}
    </section>
  );
};

export default InfoHero;
